import { useState } from 'react'
import { Description } from '../data'
import { DescriptionWidgetConfig } from '../config'
import { useWidget } from '../../../providers/widget'
import { Dialog } from '../../../components/ui/dialog'
import ResponsiveDialogContent from '../../../components/ResponsiveDialogContent'
import { useLocaleDetection } from '../../../hooks/locale'
import { useLocalizedTranslation } from '../../../common/i18n'
import { useDescriptionCss } from '../hooks/useDescriptionCss'
import { safeHtml } from '../../../common/html-safe'

interface DescriptionSectionsProps {
  description: Description
}

export default function DescriptionSections({ description }: DescriptionSectionsProps) {
  const { options, resolvedTheme } = useWidget<DescriptionWidgetConfig>()
  const { detectedLocale } = useLocaleDetection(options.language.useAutoDetectedLocale, options.language.locale)
  const { _t } = useLocalizedTranslation('description', options.translations, detectedLocale, options.language)
  const cssString = useDescriptionCss()
  const [showMoreModal, setShowMoreModal] = useState(false)

  const sections = [
    { title: 'Description', content: description.summary || description.short_summary, key: 'summary' },
    { title: 'Space', content: description.space, key: 'space' },
    { title: 'Access', content: description.access, key: 'access' },
    { title: 'Transit', content: description.transit, key: 'transit' },
    { title: 'Interaction', content: description.interaction, key: 'interaction' },
    { title: 'Neighbourhood', content: description.neighbourhood, key: 'neighbourhood' },
    { title: 'Notes', content: description.notes, key: 'notes' },
    { title: 'House Manual', content: description.house_manual, key: 'house_manual' },
  ].reduce((acc, section) => {
    if (section.content) {
      acc[section.key] = {
        title: section.title,
        content: section.content.trim(),
        key: section.key
      }
    }
    return acc
  }, {} as Record<string, { title: string, content: string, key: string }>)

  // First candidate with actual content — `??` alone let an empty-string
  // excerpt through while later candidates had text.
  const excerptHtml =
    [description[options.excerptPropertyName], description["short_summary"], sections[Object.keys(sections)[0]]?.content]
      .map(safeHtml)
      .find((html) => html !== '') ?? ''
  const hasSections = Object.keys(sections).length > 0

  // Resolved-but-empty description (row exists, every field blank — Guesty
  // sandbox properties do this): render nothing rather than an orphaned
  // "Show More" under the H1 (#996). Certainty-gated like the section title
  // in DescriptionWidgetContent — loading/error never reach this component.
  if (excerptHtml === '' && !hasSections) {
    return null
  }

  return (
    <div className={`homerunner-description-container ${resolvedTheme === 'dark' ? 'dark' : ''}`}>
      <div
        className="homerunner-description-excerpt"
        dangerouslySetInnerHTML={{ __html: excerptHtml }}
      />

      {options.showMoreButton && hasSections && (
        <div className="homerunner-description-more">
          <button
            className="bg-accent text-primary-foreground hover:bg-accent/90 px-4 py-2 rounded-md font-medium transition-colors"
            onClick={() => setShowMoreModal(true)}
          >
            {_t('showMoreButton', 'Show More')}
          </button>
        </div>
      )}

      <Dialog open={showMoreModal} onOpenChange={setShowMoreModal}>
        <ResponsiveDialogContent
          className={`homerunner-description-dialog max-h-[90vh] p-4 md:p-6 overflow-y-auto ${resolvedTheme === 'dark' ? 'dark' : ''}`}
          style={{ '--content-max-width': '1280px' } as React.CSSProperties}
          elevateDom
          cssString={cssString}
          title="Property details"
        >
          <div className="homerunner-description-dialog-content space-y-8">
            {options.displayOrder.orderInDialog.map((sectionKey) => sections[sectionKey] ? (
              <div key={sections[sectionKey].key} className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-2 lg:gap-4">
                <div className="md:col-span-1 order-1 md:order-1">
                  <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                    {sections[sectionKey].title}
                  </h3>
                </div>
                <div className="md:col-span-2 order-2 md:order-2">
                  <div
                    className="text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: safeHtml(sections[sectionKey].content) }}
                  />
                </div>
              </div>
            ) : null)}
          </div>
        </ResponsiveDialogContent>
      </Dialog>
    </div>
  )
}
